import { useMemo, useState } from 'react';

import { CampaignHeader } from './components/CampaignHeader';
import { TaskTabs } from './components/TaskTabs';
import { TaskSidebar } from './components/TaskSidebar';
import { TaskList } from './components/TaskList';
import { TaskCategory } from './types';
import {
  generateNewYearClaimTweetText,
  generateNewYearContributionClaimTweetText,
} from './utils/tweetTemplates';

import { useNewYearCampaignStatus } from '@/hooks/useNewYearCampaign';
import { useClaimLotteryReward, usePointsLotteryHistory } from '@/hooks/usePointsLottery';
import { BottomNavigation } from '@/components/BottomNavigation';
import { LotteryClaimSuccessModal } from '@/components/LotteryClaimSuccessModal';
import { TopNav } from '@/components/TopNav';
import { TweetVerificationModal } from '@/components/TweetVerificationModal';
import { lamportsToSol } from '@/utils/solana/lamportsToSol';

export type TaskPeriod = 'daily' | 'weekly' | 'campaign';

// Stored in `lottery_season.season_no` as a special value so it doesn't become the "latest" season.
const NY_LOTTERY_SEASON_NO = 0;
const NY_DISPLAY_SEASON_NO = 2026;

export default function NewYearCampaign() {
  const { data: campaignStatus, isLoading } = useNewYearCampaignStatus();
  const [activeTab, setActiveTab] = useState<TaskPeriod>('daily');
  const [activeCategory, setActiveCategory] = useState<TaskCategory | null>(null);

  // NY winners are stored in lottery_winner under a dedicated season_no=0
  const nyHistoryQuery = usePointsLotteryHistory({
    enabled: Boolean(campaignStatus?.campaign),
    season: NY_LOTTERY_SEASON_NO,
  });

  const nySeason = nyHistoryQuery.data?.season ?? null;
  const nyWinners = nyHistoryQuery.data?.winners ?? null;
  const hasNyResults = Boolean(nySeason && (nyWinners?.length ?? 0) > 0);

  const myNyWinner = useMemo(() => {
    if (!nyWinners) return null;
    return nyWinners.find((w) => w.isCurrentUser) ?? null;
  }, [nyWinners]);

  const nyPrizeSol = useMemo(() => {
    if (!myNyWinner?.prizeAmountLamports) return 0;
    return lamportsToSol(BigInt(myNyWinner.prizeAmountLamports));
  }, [myNyWinner?.prizeAmountLamports]);

  const isNyContributionOnlyWinner = useMemo(() => {
    const label = myNyWinner?.prizeLabel ?? '';
    // Our pro‑rata script inserts share-only winners as: "NY2026 Share [NY_SHARE_14]"
    return label.startsWith('NY2026 Share');
  }, [myNyWinner?.prizeLabel]);

  const nyClaimTweetText = useMemo(() => {
    if (!myNyWinner) return null;
    if (isNyContributionOnlyWinner) {
      return generateNewYearContributionClaimTweetText(
        nyPrizeSol,
        campaignStatus?.user?.rank ?? null,
      );
    }
    return generateNewYearClaimTweetText(nyPrizeSol);
  }, [myNyWinner, nyPrizeSol, isNyContributionOnlyWinner, campaignStatus?.user?.rank]);

  const [tweetVerificationOpen, setTweetVerificationOpen] = useState(false);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const claimMutation = useClaimLotteryReward({ showToast: false });

  const claimNyRewardDirectly = async () => {
    if (!nySeason?.id) return;
    await claimMutation.mutateAsync(nySeason.id);
    await nyHistoryQuery.refetch();
    setClaimModalOpen(true);
  };

  const handleClaimClick = async () => {
    if (!myNyWinner) return;

    // Already claimed: just show modal
    if (myNyWinner.isClaimed) {
      setClaimModalOpen(true);
      return;
    }

    // Verified: claim directly (marks claimed_at)
    if (myNyWinner.isVerified && myNyWinner.canClaim) {
      await claimNyRewardDirectly();
      return;
    }

    // Otherwise: require tweet verification first
    setTweetVerificationOpen(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  // Campaign not found
  if (!campaignStatus?.campaign) {
    return (
      <div className="min-h-screen" style={{ position: 'relative', fontFamily: 'Outfit, sans-serif' }}>
        <TopNav />
        <div className="container mx-auto px-4 py-12">
          <div className="bg-black/80 backdrop-blur-sm rounded-2xl p-8 border border-gray-800 max-w-md mx-auto text-center">
            <h1 className="text-3xl font-bold text-white mb-4">
              Campaign Coming Soon
            </h1>
            <p className="text-gray-400">
              The New Year Campaign will be available soon. Stay tuned!
            </p>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  const { phase } = campaignStatus.campaign;

  // Show tasks until NY winners are inserted; after that show only win/claim results.
  return (
    <div className="min-h-screen" style={{ position: 'relative', fontFamily: 'Outfit, sans-serif' }}>
      <TopNav />

      {/* Black backdrop container */}
      <div className="container mx-auto px-4 py-6 pb-24">
        <div className="bg-black/80 backdrop-blur-sm rounded-2xl border border-gray-800 overflow-hidden">
          <CampaignHeader
            prizePool={campaignStatus.campaign.prizePool}
            endDate={campaignStatus.campaign.campaignEnd}
            userPoints={campaignStatus.user?.totalPoints || 0}
            caps={campaignStatus.caps}
            totalParticipants={campaignStatus.stats.totalParticipants}
            totalPoints={campaignStatus.stats.totalPoints}
            phase={phase}
          />

          <div className="px-4 md:px-6 pb-6">
            {hasNyResults ? (
              <div className="bg-gray-900/50 rounded-2xl p-4 md:p-6 border border-gray-800">
                <h2 className="text-xl font-bold text-white mb-4">
                  New Year Lucky Prize Pool Results
                </h2>

                {nyHistoryQuery.isLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-cyan-400"></div>
                  </div>
                ) : myNyWinner ? (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-yellow-500/30 bg-black/40 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-white font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            You won!
                          </div>
                          <div className="text-gray-300 text-sm mt-1">
                            {myNyWinner.prizeLabel} ({nyPrizeSol.toFixed(4)} SOL)
                          </div>
                          <div className="text-gray-400 text-xs mt-2">
                            {myNyWinner.isClaimed && myNyWinner.claimedAt
                              ? `Claimed on ${new Date(myNyWinner.claimedAt).toLocaleDateString()}`
                              : myNyWinner.isVerified && myNyWinner.canClaim
                                ? 'Tweet verified — ready to claim'
                                : 'Tweet verification required to claim'}
                          </div>
                        </div>

                        <button
                          onClick={handleClaimClick}
                          disabled={claimMutation.isPending}
                          className="px-5 py-2 rounded-full font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            fontFamily: 'Outfit, sans-serif',
                            background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 50%, #F59E0B 100%)',
                            color: '#000',
                          }}
                        >
                          {claimMutation.isPending
                            ? 'Processing...'
                            : myNyWinner.isClaimed
                              ? 'View Reward'
                              : myNyWinner.isVerified && myNyWinner.canClaim
                                ? 'Claim Reward'
                                : 'Verify & Claim'}
                        </button>
                      </div>
                    </div>

                    <div className="rounded-xl border border-gray-700 bg-black/30 p-4 text-sm text-gray-300">
                      This draw is final. If you won, you can claim here by sharing on X and verifying your tweet.
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-gray-700 bg-black/30 p-6 text-center">
                    <div className="text-white font-semibold mb-2">
                      Not selected this time
                    </div>
                    <div className="text-gray-400 text-sm">
                      You didn&apos;t win the New Year Lucky Prize Pool draw.
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-900/50 rounded-2xl p-4 md:p-6 border border-gray-800">
                <h2 className="text-xl font-bold text-white mb-4">
                  Campaign Tasks
                </h2>

                <div className="flex flex-col md:flex-row gap-4">
                  {/* Sidebar - Categories */}
                  <TaskSidebar
                    activeCategory={activeCategory}
                    onCategoryChange={setActiveCategory}
                  />

                  {/* Main content */}
                  <div className="flex-1">
                    {/* Tabs */}
                    <TaskTabs
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                    />

                    {/* Task List */}
                    <TaskList
                      period={activeTab}
                      category={activeCategory}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNavigation />

      {/* Tweet verification + claim (NY season) */}
      {hasNyResults && myNyWinner && nySeason && (
        <>
          <TweetVerificationModal
            isOpen={tweetVerificationOpen}
            onClose={() => setTweetVerificationOpen(false)}
            type="lottery"
            winnerId={myNyWinner.id}
            seasonId={nySeason.id}
            tweetTextOverride={nyClaimTweetText ?? undefined}
            onSuccess={() => {
              setTweetVerificationOpen(false);
              claimNyRewardDirectly();
            }}
          />

          <LotteryClaimSuccessModal
            open={claimModalOpen}
            onClose={() => {
              setClaimModalOpen(false);
              nyHistoryQuery.refetch();
            }}
            reward={{
              prizeLabel: myNyWinner.prizeLabel,
              prizeAmountLamports: myNyWinner.prizeAmountLamports,
              seasonNo: NY_DISPLAY_SEASON_NO,
            }}
          />
        </>
      )}
    </div>
  );
}
